import React from 'react'
import PropTypes from 'prop-types'
import { Button, Nav, NavItem, Tab, Row, Col, Card } from 'react-bootstrap'
import { Provider, observer } from "mobx-react"
import FormContainer from './components/FormContainer.js'
import FormBody from './components/FormBody.js'
import FormSidebar from './components/FormSidebar.js'
import FaIcon from '../../helpers/fa_icon.js'
import FieldGroup from './components/FieldGroup.js'
import EventTypeSelector from '../EventTypeSelector'
import SaveButton from './components/SaveButton'
import EditableTitle from './components/EditableTitle.js'
import Visibility from './components/Visibility'
import SendForm from './components/SendForm.js'
import SendFormBtn from './components/SendFormBtn.js'
import Notification from './components/Notification.js'
import PrintForm from './components/PrintForm.js'
import MoreOptions from './components/MoreOptions.js'
import ErrorBadge from './components/ErrorBadge.js'
import AddTabButton from './components/AddTabButton'
import { FloatingPlayer } from '../ui/SongPreview'
import ObservableStore from './stores/store.js'
import './style.scss'

const autosaveIntervalSeconds = 30

@observer
export default class FormBuilder extends React.Component {
  static propTypes = {
    data: PropTypes.object,
    formTemplateId: PropTypes.number,
    formId: PropTypes.number,
    eventId: PropTypes.number,
    mode: PropTypes.string,
    disableEdit: PropTypes.bool,
    unlockable: PropTypes.bool,
    complete: PropTypes.bool,
    printVersion: PropTypes.bool,
    values: PropTypes.object,
    allCategories: PropTypes.bool,
    eventCategories: PropTypes.array,
    allAddOnCategories: PropTypes.bool,
    addOnCategories: PropTypes.array,
    extraCategories: PropTypes.array,
    autosave: PropTypes.bool,
    apiKey: PropTypes.string.isRequired,
    dateFormat: PropTypes.string.isRequired,
    timeFormat: PropTypes.string.isRequired,
    suggestionLists: PropTypes.array,
    selectedEventCategoryIds: PropTypes.array,
    selectedPackageGroupIds: PropTypes.array,
    selectedPackageIds: PropTypes.array,
    selectedAddOnCategoryIds: PropTypes.array,
    selectedAddOnIds: PropTypes.array,
    selectedBackdropIds: PropTypes.array,
    selectedExtraCategoryIds: PropTypes.array,
    selectedExtraGroupIds: PropTypes.array,
    selectedExtraIds: PropTypes.array,
    managePaths: PropTypes.array,
    playlists: PropTypes.array,
    supportsExtras: PropTypes.bool
  }

  static defaultProps = {
    autosave: false,
    isHistoricalVersion: false
  }

  constructor(props) {
    super(props)

    let data = this.props.data || {}
    this.store = new ObservableStore()
    this.store.apiKey = this.props.apiKey
    this.store.formTemplateId = this.props.formTemplateId
    this.store.formId = this.props.formId
    this.store.eventId = this.props.eventId
    this.store.mode = this.props.mode
    this.store.unlockable = this.props.unlockable
    this.store.disableEdit = this.props.disableEdit
    this.store.title = data.title || "New Questionnaire"
    this.store.fieldGroups = data.fieldGroups || []
    this.store.complete = this.props.complete
    this.store.printVersion = this.props.printVersion
    this.store.dateFormat = this.props.dateFormat
    this.store.timeFormat = this.props.timeFormat
    this.store.suggestionLists = this.props.suggestionLists
    this.store.selectedEventCategoryIds = this.props.selectedEventCategoryIds
    this.store.selectedPackageGroupIds = this.props.selectedPackageGroupIds
    this.store.selectedPackageIds = this.props.selectedPackageIds
    this.store.selectedAddOnCategoryIds = this.props.selectedAddOnCategoryIds
    this.store.selectedAddOnIds = this.props.selectedAddOnIds
    this.store.selectedBackdropIds = this.props.selectedBackdropIds
    this.store.selectedExtraCategoryIds = this.props.selectedExtraCategoryIds
    this.store.selectedExtraGroupIds = this.props.selectedExtraGroupIds
    this.store.selectedExtraIds = this.props.selectedExtraIds
    this.store.playlists = this.props.playlists
    if(this.props.values) {
      Object.entries(this.props.values).map((entry) => {
        let key = entry[0]
        // Build an array of Maps
        let value = entry[1].map((obj) => new Map(Object.entries(obj)))
        this.store.values.set(key, value)
      })
    }
    this.store._previousStringifiedData = this.store._stringifiedData
    this.store._previousStringifiedTemplate = this.store._stringifiedTemplate

    this.store.eventTypeSelectorSettings = {
      eventCategories: props.eventCategories,
      addOnCategories: props.addOnCategories,
      extraCategories: props.extraCategories,
    }
  }

  handleEventTypeSelectorChange = (change) => {
    this.store.allCategories = change.allCategories
    this.store.eventCategoryIds = change.eventCategoryIds
    this.store.packageGroupIds = change.packageGroupIds
    this.store.eventTypeIds = change.eventTypeIds
    this.store.allMiniSessions = change.allMiniSessions
    this.store.miniSessionIds = change.miniSessionIds
    this.store.allAddOnCategories = change.allAddOnCategories
    this.store.addOnCategoryIds = change.addOnCategoryIds
    this.store.addOnIds = change.addOnIds
    this.store.extraCategoryIds = change.extraCategoryIds
    this.store.extraGroupIds = change.extraGroupIds
    this.store.extraIds = change.extraIds
  }

  componentDidMount() {
    this.autosaveTimer = setInterval(() => this.autosave(), autosaveIntervalSeconds * 1000)
  }

  componentWillUnmount() {
    if(this.autosaveTimer) {
      clearTimeout(this.autosaveTimer)
    }
  }

  autosave = () => {
    const { autosave } = this.props

    if(autosave && this.store.editable && !this.store.previewable && this.store.allowSave) {
      this.store.persistFormContent(null, true)
    }
  }

  handleTabSelected = (key) => {
    this.store.activeFieldGroup = key
    this.autosave()
  }

  render() {
    let fieldGroups = this.store.fieldGroups
    let showSidebar = !this.store.previewable && (this.store.buildable || this.store.editable || !this.store.printVersion)

    return (
      <Provider store={this.store}>
        <div className={`form-builder form-builder-${this.store.mode}-mode`}>
          <FloatingPlayer>
            <Notification />
            <EditableTitle />
            <FormContainer showSidebar={showSidebar}>
              <FormBody showSidebar={showSidebar}>
                { this.store.buildable &&
                <div className="text-end">
                  <AddTabButton />
                </div>
                }
                <Tab.Container
                  id="form-builder-tab-container"
                  activeKey={this.store.activeFieldGroup}
                  onSelect={this.handleTabSelected}
                >
                  <div className="form-builder-content">
                    { fieldGroups.length > 1 &&
                    <Visibility hideOnPrint={true}>
                      <Row className="clearfix">
                        <Col xs={12}>
                          <Nav variant="tabs">
                            {fieldGroups.map((fieldGroup, fieldGroupIndex) =>
                            <NavItem key={fieldGroupIndex}>
                              <Nav.Link eventKey={`tab-${fieldGroupIndex}`}>
                                { fieldGroup.name }
                                <ErrorBadge errorCount={this.store.fieldGroupErrorCounts.get(fieldGroupIndex)} />
                              </Nav.Link>
                            </NavItem>
                            )}

                          </Nav>
                        </Col>
                      </Row>
                    </Visibility>
                    }
                    <Visibility hideOnPrint={true}>
                      <Tab.Content>
                        {fieldGroups.map((fieldGroup, fieldGroupIndex) =>
                        <Tab.Pane key={fieldGroupIndex} eventKey={`tab-${fieldGroupIndex}`}>
                          <FieldGroup key={fieldGroupIndex} fieldGroup={fieldGroup} fieldGroupIndex={fieldGroupIndex} />
                        </Tab.Pane>
                        )}
                      </Tab.Content>
                    </Visibility>
                    <Visibility showOnPrint={true}>
                      {fieldGroups.map((fieldGroup, fieldGroupIndex) =>
                      <div className="tab-section" key={fieldGroupIndex}>
                        <h3>{ fieldGroup.name }</h3>
                        <FieldGroup fieldGroup={fieldGroup} fieldGroupIndex={fieldGroupIndex} />
                        <div className="page-break" />
                      </div>
                      )}
                    </Visibility>

                    { !this.store.printVersion &&
                      <div className="form-builder-footer">
                        { this.store.buildable && 
                        <div className="mt-3 flex-2">
                          <SaveButton text="Save Template" onClick={() => this.store.persistFormTemplate()} />
                        </div>
                        }
                        { this.store.editable && !this.store.previewable &&
                          <div className="mt-3 flex-2">
                            <SaveButton onClick={() => this.store.persistFormContent()} />
                          </div>
                        }
                        { (this.store.previewable && !this.store.disableEdit) &&
                          <div className="mt-3 flex-2">
                            <Button className="btn-lg btn-call-to-action w-100" onClick={() => this.store.exitPreviewMode()}>Back to Editor</Button>
                          </div>
                        }
                        <div className="mt-3 flex-1">
                          <SendFormBtn />
                        </div>
                      </div>
                    }
                  </div>
                </Tab.Container>
              </FormBody>
              <FormSidebar showSidebar={showSidebar}>
                { this.store.buildable &&
                <Card className="mb-2">
                  <Card.Header>
                    <Card.Title>Edit Mode</Card.Title>
                  </Card.Header>
                  <Card.Body>
                    { this.props.eventId ?
                      <p>Any changes made to this questionnaire will only apply to this booking's copy of the questionnaire.</p>
                      :
                      <>
                        <p>Any changes made to this template will apply to all future bookings.</p>
                        <Button variant="success" className="w-100 spacer-bottom" onClick={() => this.store.enterPreviewMode()}>Live Preview</Button>
                      </>
                    }
                  </Card.Body>
                </Card>
                }

                { (this.store.buildable && this.props.eventCategories && !this.props.eventId) &&
                  <Card>
                    <Card.Header>
                      <Card.Title>Show for the following:</Card.Title>
                    </Card.Header>
                    <Card.Body>
                      <EventTypeSelector
                        allCategories={this.props.allCategories}
                        eventCategories={this.props.eventCategories}
                        supportsPackages={true}
                        supportsMiniSessions={this.props.supportsMiniSessions}
                        supportsAddOns={true}
                        supportsExtras={this.props.supportsExtras}
                        allMiniSessions={this.props.allMiniSessions}
                        miniSessions={this.props.miniSessions}
                        allAddOnCategories={this.props.allAddOnCategories}
                        addOnCategories={this.props.addOnCategories}
                        extraCategories={this.props.extraCategories}
                        onChange={this.handleEventTypeSelectorChange}
                      />
                    </Card.Body>
                  </Card>
                }

                { (this.store.editable && !this.store.previewable && !this.store.printVersion) &&
                  <div className="mt-3 d-none d-sm-block">
                    <SaveButton onClick={() => this.store.persistFormContent()} />
                  </div>
                }
                { !this.store.printVersion && 
                  <div className="mt-3">
                    <PrintForm />
                  </div>
                }

                { !this.store.printVersion && 
                  <MoreOptions managePaths={this.props.managePaths} />
                }

                { !this.store.printVersion && 
                  <div className="mt-3">
                    <SendForm />
                  </div>
                }
              </FormSidebar>
            </FormContainer>
          </FloatingPlayer>
        </div>
      </Provider>
    )
  }
}
